import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { SOURCES, CONFIGURATIONS, LOCALITIES } from "../constants/dropdownOptions";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Plus, ArrowLeft, Zap, GitBranch, Play, Trash2, Save, Power, PowerOff,
  User, Mail, MessageSquare, Bell, CalendarPlus, Target, Star,
} from "lucide-react";

// ============ CUSTOM NODES ============

const TriggerNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-xl border-2 min-w-[180px] shadow-md ${selected ? "border-violet-500 ring-2 ring-violet-200" : "border-violet-300"} bg-gradient-to-br from-violet-50 to-violet-100`}>
    <div className="flex items-center gap-2 mb-1">
      <Zap className="w-4 h-4 text-violet-600" />
      <span className="text-xs font-bold text-violet-700 uppercase tracking-wide">Trigger</span>
    </div>
    <p className="text-sm font-semibold text-slate-800">{data.label || "When..."}</p>
    {data.config?.source && <p className="text-[10px] text-slate-500 mt-0.5">Source: {data.config.source}</p>}
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white" />
  </div>
);

const ConditionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-xl border-2 min-w-[180px] shadow-md ${selected ? "border-amber-500 ring-2 ring-amber-200" : "border-amber-300"} bg-gradient-to-br from-amber-50 to-amber-100`} style={{ transform: "rotate(0deg)" }}>
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white" />
    <div className="flex items-center gap-2 mb-1">
      <GitBranch className="w-4 h-4 text-amber-600" />
      <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Condition</span>
    </div>
    <p className="text-sm font-semibold text-slate-800">{data.label || "If..."}</p>
    {data.conditions?.[0] && <p className="text-[10px] text-slate-500 mt-0.5">{data.conditions[0].field} {data.conditions[0].operator} {data.conditions[0].value}</p>}
    <div className="flex justify-between mt-2">
      <Handle type="source" position={Position.Bottom} id="true" className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white !left-[30%]" />
      <Handle type="source" position={Position.Bottom} id="false" className="!w-3 !h-3 !bg-red-400 !border-2 !border-white !left-[70%]" />
    </div>
    <div className="flex justify-between mt-1 px-1">
      <span className="text-[9px] text-emerald-600 font-bold">YES</span>
      <span className="text-[9px] text-red-500 font-bold">NO</span>
    </div>
  </div>
);

const ACTION_ICONS = {
  assign_rep: User, assign_team: User, change_stage: Target, update_score: Star,
  add_task: CalendarPlus, send_whatsapp: MessageSquare, send_email: Mail,
  notify_manager: Bell, schedule_followup: CalendarPlus,
};

const ActionNode = ({ data, selected }) => {
  const Icon = ACTION_ICONS[data.action_type] || Play;
  return (
    <div className={`px-4 py-3 rounded-xl border-2 min-w-[180px] shadow-md ${selected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-emerald-300"} bg-gradient-to-br from-emerald-50 to-emerald-100`}>
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white" />
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-emerald-600" />
        <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Action</span>
      </div>
      <p className="text-sm font-semibold text-slate-800">{data.label || "Do..."}</p>
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white" />
    </div>
  );
};

const nodeTypes = { trigger: TriggerNode, condition: ConditionNode, action: ActionNode };

const TRIGGER_OPTIONS = [
  { value: "new_lead", label: "New Lead Created" },
  { value: "stage_change", label: "Stage Changed" },
  { value: "re_enquiry", label: "Re-enquiry Detected" },
  { value: "score_change", label: "Score Changed" },
  { value: "time_based", label: "Time-based (Inactivity)" },
];

const ACTION_OPTIONS = [
  { value: "assign_rep", label: "Assign to Rep" },
  { value: "assign_team", label: "Assign to Team (Round Robin)" },
  { value: "change_stage", label: "Change Stage" },
  { value: "update_score", label: "Update Score" },
  { value: "add_task", label: "Add Task" },
  { value: "send_whatsapp", label: "Send WhatsApp" },
  { value: "send_email", label: "Send Email" },
  { value: "notify_manager", label: "Notify Manager" },
  { value: "schedule_followup", label: "Schedule Follow-up" },
];

const FIELD_OPTIONS = [
  { value: "source", label: "Source" },
  { value: "project_interest", label: "Project" },
  { value: "budget_max", label: "Budget (Max)" },
  { value: "budget_min", label: "Budget (Min)" },
  { value: "score", label: "Score" },
  { value: "stage", label: "Stage" },
  { value: "locality_preference", label: "Locality" },
  { value: "configuration", label: "Configuration" },
  { value: "reinquiry_count", label: "Re-enquiry Count" },
];

const STAGES = ["new","contacted","qualified","site_visit_scheduled","site_visit","revisit","negotiation_scheduled","negotiation","booked","re_engaged","lost"];
const STAGE_OPTIONS = STAGES.map(s => ({ value: s, label: s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) }));
const SCORE_OPTIONS = [
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "mild", label: "Mild" },
  { value: "cold", label: "Cold" },
  { value: "nurture", label: "Nurture" },
];

// Map condition fields to their dropdown options
const FIELD_VALUE_OPTIONS = {
  source: SOURCES,
  stage: STAGE_OPTIONS,
  score: SCORE_OPTIONS,
  configuration: CONFIGURATIONS,
  locality_preference: LOCALITIES.map(l => ({ value: l, label: l })),
};

const OP_OPTIONS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
  { value: "contains", label: "contains" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "gte", label: ">=" },
  { value: "lte", label: "<=" },
  { value: "in", label: "in (comma-separated)" },
];

// ============ NODE CONFIG PANEL ============

const NodeConfigPanel = ({ node, onUpdate, onDelete, teams, users }) => {
  if (!node) return null;
  const d = node.data;

  const update = (key, val) => {
    onUpdate(node.id, { ...d, [key]: val });
  };
  const updateConfig = (key, val) => {
    onUpdate(node.id, { ...d, config: { ...(d.config || {}), [key]: val } });
  };

  return (
    <div className="w-72 bg-white border-l border-slate-200 p-4 overflow-y-auto shrink-0" data-testid="node-config-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-800 capitalize">{node.type} Config</h3>
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => onDelete(node.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Label</label>
          <Input value={d.label || ""} onChange={(e) => update("label", e.target.value)} className="h-8 text-sm" data-testid="node-label-input" />
        </div>

        {node.type === "trigger" && (
          <>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Trigger Type</label>
              <Select value={d.trigger_type || "new_lead"} onValueChange={(v) => update("trigger_type", v)}>
                <SelectTrigger className="h-8 text-sm" data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {d.trigger_type === "new_lead" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Source filter (optional)</label>
                <Select value={d.config?.source || "_any"} onValueChange={(v) => updateConfig("source", v === "_any" ? "" : v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Any source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_any">Any Source</SelectItem>
                    {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {d.trigger_type === "stage_change" && (
              <>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">From Stage</label>
                  <Select value={d.config?.from_stage || "_any"} onValueChange={(v) => updateConfig("from_stage", v === "_any" ? "" : v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Any stage" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">Any Stage</SelectItem>
                      {STAGE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">To Stage</label>
                  <Select value={d.config?.to_stage || "_any"} onValueChange={(v) => updateConfig("to_stage", v === "_any" ? "" : v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Any stage" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">Any Stage</SelectItem>
                      {STAGE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {d.trigger_type === "score_change" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Score Threshold</label>
                <Select value={d.config?.score_threshold || "_any"} onValueChange={(v) => updateConfig("score_threshold", v === "_any" ? "" : v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Any score" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_any">Any Score</SelectItem>
                    {SCORE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {d.trigger_type === "time_based" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Inactive for</label>
                <Select value={String(d.config?.inactive_hours || "24")} onValueChange={(v) => updateConfig("inactive_hours", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="12">12 hours</SelectItem>
                    <SelectItem value="24">1 day</SelectItem>
                    <SelectItem value="48">2 days</SelectItem>
                    <SelectItem value="72">3 days</SelectItem>
                    <SelectItem value="120">5 days</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                    <SelectItem value="336">14 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}

        {node.type === "condition" && (
          <>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Logic</label>
              <Select value={d.logic || "AND"} onValueChange={(v) => update("logic", v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">All conditions (AND)</SelectItem>
                  <SelectItem value="OR">Any condition (OR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(d.conditions || [{}]).map((c, i) => (
              <div key={i} className="bg-slate-50 p-2 rounded-lg space-y-1.5">
                <Select value={c.field || ""} onValueChange={(v) => {
                  const conds = [...(d.conditions || [{}])];
                  conds[i] = { ...conds[i], field: v, value: "" };
                  update("conditions", conds);
                }}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
                  <SelectContent>{FIELD_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={c.operator || "equals"} onValueChange={(v) => {
                  const conds = [...(d.conditions || [{}])];
                  conds[i] = { ...conds[i], operator: v };
                  update("conditions", conds);
                }}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{OP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                {FIELD_VALUE_OPTIONS[c.field] ? (
                  <Select value={c.value || ""} onValueChange={(v) => {
                    const conds = [...(d.conditions || [{}])];
                    conds[i] = { ...conds[i], value: v };
                    update("conditions", conds);
                  }}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select value" /></SelectTrigger>
                    <SelectContent>{FIELD_VALUE_OPTIONS[c.field].map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={c.value || ""}
                    onChange={(e) => {
                      const conds = [...(d.conditions || [{}])];
                      conds[i] = { ...conds[i], value: e.target.value };
                      update("conditions", conds);
                    }}
                    placeholder={c.field === "budget_max" || c.field === "budget_min" ? "e.g. 5000000" : c.field === "reinquiry_count" ? "e.g. 2" : "Value"}
                    className="h-7 text-xs"
                  />
                )}
                {(d.conditions || []).length > 1 && (
                  <Button variant="ghost" size="sm" className="text-xs text-red-500 h-6 px-1" onClick={() => {
                    const conds = [...(d.conditions || [])];
                    conds.splice(i, 1);
                    update("conditions", conds);
                  }}>Remove</Button>
                )}
              </div>
            ))}
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => update("conditions", [...(d.conditions || []), {}])}>+ Add condition</Button>
          </>
        )}

        {node.type === "action" && (
          <>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Action</label>
              <Select value={d.action_type || ""} onValueChange={(v) => update("action_type", v)}>
                <SelectTrigger className="h-8 text-sm" data-testid="action-type-select"><SelectValue placeholder="Select action" /></SelectTrigger>
                <SelectContent>{ACTION_OPTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {d.action_type === "assign_rep" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Rep</label>
                <Select value={d.config?.user_id || ""} onValueChange={(v) => updateConfig("user_id", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select rep" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {d.action_type === "assign_team" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Team</label>
                <Select value={d.config?.team_id || ""} onValueChange={(v) => updateConfig("team_id", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>{teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {d.action_type === "change_stage" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Stage</label>
                <Select value={d.config?.stage || ""} onValueChange={(v) => updateConfig("stage", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent>
                    {STAGE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {d.action_type === "update_score" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Score</label>
                <Select value={d.config?.score || ""} onValueChange={(v) => updateConfig("score", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select score" /></SelectTrigger>
                  <SelectContent>
                    {SCORE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(d.action_type === "add_task" || d.action_type === "schedule_followup") && (
              <>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Task Title</label>
                  <Input value={d.config?.title || ""} onChange={(e) => updateConfig("title", e.target.value)} placeholder='e.g. Follow up with lead' className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Priority</label>
                  <Select value={d.config?.priority || "medium"} onValueChange={(v) => updateConfig("priority", v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {d.action_type === "schedule_followup" && (
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Days from now</label>
                    <Select value={String(d.config?.days || "1")} onValueChange={(v) => updateConfig("days", v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,5,7,10,14,21,30].map(n => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "day" : "days"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            {d.action_type === "send_whatsapp" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Template (use {"{{name}}"}, {"{{project}}"})</label>
                <textarea value={d.config?.template || ""} onChange={(e) => updateConfig("template", e.target.value)} className="w-full h-20 text-xs border rounded-lg p-2 resize-none" />
              </div>
            )}
            {d.action_type === "send_email" && (
              <>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Subject</label>
                  <Input value={d.config?.subject || ""} onChange={(e) => updateConfig("subject", e.target.value)} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Body</label>
                  <textarea value={d.config?.body || ""} onChange={(e) => updateConfig("body", e.target.value)} className="w-full h-20 text-xs border rounded-lg p-2 resize-none" />
                </div>
              </>
            )}
            {d.action_type === "notify_manager" && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Notification Message</label>
                <Input value={d.config?.message || ""} onChange={(e) => updateConfig("message", e.target.value)} className="h-8 text-sm" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============

export default function WorkflowBuilderPage() {
  const [workflows, setWorkflows] = useState([]);
  const [activeWf, setActiveWf] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newWfName, setNewWfName] = useState("");
  const [newWfCategory, setNewWfCategory] = useState("general");
  const [newWfTrigger, setNewWfTrigger] = useState("new_lead");
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const nodeCountRef = useRef(0);

  useEffect(() => {
    fetchWorkflows();
    axios.get(`${API}/teams`).then(r => setTeams(r.data || [])).catch(() => {});
    axios.get(`${API}/users`).then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/visual-workflows`);
      setWorkflows(res.data || []);
    } catch { toast.error("Failed to load workflows"); }
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      id: `e_${Date.now()}`,
      animated: true,
      style: { stroke: "#94a3b8", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
      label: params.sourceHandle === "false" ? "No" : params.sourceHandle === "true" ? "Yes" : "",
    }, eds));
  }, [setEdges]);

  const createWorkflow = async () => {
    if (!newWfName.trim()) return;
    try {
      const triggerNode = {
        id: "trigger_1",
        type: "trigger",
        position: { x: 250, y: 50 },
        data: { label: TRIGGER_OPTIONS.find(t => t.value === newWfTrigger)?.label || "Trigger", trigger_type: newWfTrigger, config: {} },
      };
      const res = await axios.post(`${API}/visual-workflows`, {
        name: newWfName.trim(),
        category: newWfCategory,
        trigger_type: newWfTrigger,
        nodes: [triggerNode],
        edges: [],
      });
      toast.success("Workflow created");
      setShowNewDialog(false);
      setNewWfName("");
      await fetchWorkflows();
      openWorkflow(res.data);
    } catch { toast.error("Failed to create workflow"); }
  };

  const openWorkflow = (wf) => {
    setActiveWf(wf);
    setNodes(wf.nodes || []);
    setEdges((wf.edges || []).map(e => ({
      ...e,
      animated: true,
      style: { stroke: "#94a3b8", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
    })));
    setSelectedNode(null);
    nodeCountRef.current = (wf.nodes || []).length;
  };

  const saveWorkflow = async () => {
    if (!activeWf) return;
    setSaving(true);
    try {
      await axios.put(`${API}/visual-workflows/${activeWf.workflow_id}`, {
        nodes, edges,
        trigger_type: nodes.find(n => n.type === "trigger")?.data?.trigger_type || activeWf.trigger_type,
      });
      toast.success("Workflow saved");
      fetchWorkflows();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const addNode = (type) => {
    nodeCountRef.current += 1;
    const id = `${type}_${nodeCountRef.current}_${Date.now()}`;
    const y = type === "condition" ? 200 : 350;
    const newNode = {
      id,
      type,
      position: { x: 200 + Math.random() * 200, y: y + Math.random() * 100 },
      data: {
        label: type === "condition" ? "If..." : "Do...",
        ...(type === "condition" ? { conditions: [{}], logic: "AND" } : {}),
        ...(type === "action" ? { action_type: "", config: {} } : {}),
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const updateNodeData = (nodeId, newData) => {
    setNodes((nds) => nds.map(n => n.id === nodeId ? { ...n, data: newData } : n));
    if (selectedNode?.id === nodeId) setSelectedNode(prev => ({ ...prev, data: newData }));
  };

  const deleteNode = (nodeId) => {
    setNodes((nds) => nds.filter(n => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  };

  const toggleWorkflow = async (wfId) => {
    try {
      const res = await axios.patch(`${API}/visual-workflows/${wfId}/toggle`);
      toast.success(res.data.is_active ? "Workflow activated" : "Workflow deactivated");
      fetchWorkflows();
    } catch { toast.error("Failed to toggle"); }
  };

  const deleteWorkflow = async (wfId) => {
    try {
      await axios.delete(`${API}/visual-workflows/${wfId}`);
      toast.success("Workflow deleted");
      if (activeWf?.workflow_id === wfId) setActiveWf(null);
      fetchWorkflows();
    } catch { toast.error("Failed to delete"); }
  };

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);

  // ============ RENDER ============

  if (activeWf) {
    return (
      <Layout>
        <div className="flex h-[calc(100vh-64px)]" data-testid="workflow-builder">
          {/* Toolbar */}
          <div className="w-14 bg-slate-900 flex flex-col items-center py-3 gap-3 shrink-0">
            <button onClick={() => setActiveWf(null)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors" title="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-px bg-slate-700" />
            <button onClick={() => addNode("condition")} className="p-2 rounded-lg text-amber-400 hover:text-amber-300 hover:bg-slate-700 transition-colors" title="Add Condition" data-testid="add-condition-btn">
              <GitBranch className="w-5 h-5" />
            </button>
            <button onClick={() => addNode("action")} className="p-2 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-slate-700 transition-colors" title="Add Action" data-testid="add-action-btn">
              <Play className="w-5 h-5" />
            </button>
            <div className="flex-1" />
            <button onClick={saveWorkflow} disabled={saving} className="p-2 rounded-lg text-blue-400 hover:text-blue-300 hover:bg-slate-700 transition-colors" title="Save" data-testid="save-workflow-btn">
              <Save className="w-5 h-5" />
            </button>
          </div>

          {/* Canvas */}
          <div className="flex-1 relative">
            <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm border">
              <span className="text-sm font-semibold text-slate-800">{activeWf.name}</span>
              <Badge className="ml-2 text-[10px]" variant={activeWf.is_active ? "default" : "secondary"}>{activeWf.is_active ? "Active" : "Inactive"}</Badge>
            </div>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={() => setSelectedNode(null)}
              nodeTypes={nodeTypes}
              fitView
              snapToGrid
              snapGrid={[15, 15]}
              defaultEdgeOptions={{ animated: true }}
            >
              <Background variant="dots" gap={15} size={1} color="#cbd5e1" />
              <Controls className="!bg-white !border-slate-200 !shadow-sm" />
              <MiniMap className="!bg-slate-50 !border-slate-200" nodeColor={(n) => n.type === "trigger" ? "#8b5cf6" : n.type === "condition" ? "#f59e0b" : "#10b981"} />
            </ReactFlow>
          </div>

          {/* Config Panel */}
          {selectedNode && (
            <NodeConfigPanel
              node={selectedNode}
              onUpdate={updateNodeData}
              onDelete={deleteNode}
              teams={teams}
              users={users}
            />
          )}
        </div>
      </Layout>
    );
  }

  // ============ WORKFLOW LIST ============

  const grouped = {};
  workflows.forEach(wf => {
    const cat = wf.category || "general";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(wf);
  });

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-[1200px] mx-auto space-y-6" data-testid="workflow-list-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Workflow Builder</h1>
            <p className="text-sm text-slate-500 mt-1">Design automation flows visually with triggers, conditions, and actions</p>
          </div>
          <Button onClick={() => setShowNewDialog(true)} data-testid="new-workflow-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Workflow
          </Button>
        </div>

        {workflows.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <GitBranch className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-500">No workflows yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your first automation workflow</p>
              <Button className="mt-4" onClick={() => setShowNewDialog(true)}>Create Workflow</Button>
            </CardContent>
          </Card>
        ) : (
          Object.entries(grouped).map(([cat, wfs]) => (
            <div key={cat}>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{cat}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {wfs.map(wf => (
                  <Card key={wf.workflow_id} className="hover:shadow-md transition-shadow cursor-pointer group" data-testid={`workflow-card-${wf.workflow_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base" onClick={() => openWorkflow(wf)}>{wf.name}</CardTitle>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); toggleWorkflow(wf.workflow_id); }} title={wf.is_active ? "Deactivate" : "Activate"} className="p-1 rounded hover:bg-slate-100">
                            {wf.is_active ? <Power className="w-4 h-4 text-emerald-500" /> : <PowerOff className="w-4 h-4 text-slate-400" />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteWorkflow(wf.workflow_id); }} className="p-1 rounded hover:bg-red-50">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent onClick={() => openWorkflow(wf)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] capitalize">{wf.trigger_type?.replace(/_/g, ' ')}</Badge>
                        <Badge className={`text-[10px] ${wf.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {wf.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <span className="text-[10px] text-slate-400 ml-auto">{(wf.nodes?.length || 0)} nodes</span>
                      </div>
                      {wf.description && <p className="text-xs text-slate-500 mt-2">{wf.description}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}

        {/* New Workflow Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Workflow</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Name</label>
                <Input value={newWfName} onChange={(e) => setNewWfName(e.target.value)} placeholder="e.g. Route Facebook leads to Team A" data-testid="new-wf-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
                <Input value={newWfCategory} onChange={(e) => setNewWfCategory(e.target.value)} placeholder="e.g. routing, nurture, alerts" data-testid="new-wf-category" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Trigger</label>
                <Select value={newWfTrigger} onValueChange={setNewWfTrigger}>
                  <SelectTrigger data-testid="new-wf-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIGGER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
              <Button onClick={createWorkflow} disabled={!newWfName.trim()} data-testid="create-wf-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
